import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';

import { Image, ImageLoadState } from '@fluentui/react/lib/Image';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Loading } from '@bingads-webui-campaign-react/smart-component-primitive';
import { useClientContext } from '@bingads-webui-campaign-react/smart-page-context';

const getStyles = () => ({
  img: {
    '& img': {
      maxWidth: '100%',
    },
  },
  loading: {
    position: 'absolute',
    top: 0,
  },
});

const getDelay = (retryCount) => {
  if (retryCount === 2) {
    return 3000;
  } else if (retryCount === 1) {
    return 5000;
  }

  return 1000;
};

const UnstyledSmartImageView = (props) => {
  const {
    src,
    classes,
    className,
    showLoading,
    retryLimit,
    ...rest
  } = props;
  const [isLoading, setIsLoading] = useState(showLoading);
  const [refreshKey, setRefreshKey] = useState(Date.now());
  const { isRuntime } = useClientContext();

  // For bing.com or bing-exp.com image, when it returns 404, it still returns a broken image, which couldn't trigger onerror event on image tag
  const refetchImage = useCallback((count) => {
    fetch(src).then((response) => {
      if (!response.ok && response.status === 404 && count > 0) {
        setTimeout(() => refetchImage(count - 1), getDelay(count));
        // We retry and the image return ok, so we should refresh the image element
      } else if (response.ok && count < retryLimit) {
        setRefreshKey(Date.now());
      }
    });
  }, [retryLimit, src]);

  useEffect(() => {
    // When we upload new image from BulkUpload api and read it immediately after upload, Japan user may get 404 error
    // This is because the ObjectStore data replication delay, so we retry to fetch the image
    // https://eng.ms/docs/experiences-devices/webxt/search-content-platform/objectstore/objectstore/objectstore-public-wiki/overview/objectstore-consistency
    if (!isRuntime && retryLimit > 0) {
      setTimeout(refetchImage(Math.min(retryLimit, 3)), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLoadingStateChange = (state) => {
    if (state === ImageLoadState.loaded) {
      setIsLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Image
        key={refreshKey}
        className={classnames(classes.img, className)}
        onLoadingStateChange={showLoading ? onLoadingStateChange : _.noop}
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        {...rest}
      />
      {
        isLoading && <Loading className={classes.loading} />
      }
    </React.Fragment>
  );
};

UnstyledSmartImageView.propTypes = {
  src: PropTypes.string.isRequired,
  classes: PropTypes.shape({
    img: PropTypes.string.isRequired,
  }).isRequired,
  className: PropTypes.string,
  showLoading: PropTypes.bool,
  retryLimit: PropTypes.number,
};

UnstyledSmartImageView.defaultProps = {
  className: undefined,
  showLoading: false,
  retryLimit: 0,
};

UnstyledSmartImageView.displayName = 'SmartImageView';

export const SmartImageView = withDefaultStyles(UnstyledSmartImageView, getStyles);
